import { daysOfTheWeekP, generalHelpers, months, typesOfPreaching } from '@monorepo/helpers';
import { FC, useEffect, useMemo, useState } from 'react';
import { helpers, userServices } from 'src/services';
import { Loading } from 'src/components/_commons';
import { Modal } from 'react-bootstrap';
import { setAlertResponse } from 'src/store';
import { typeCartPeriod, typeMeeting, typeMeetingsDoc, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetIsDarkMode, useGetIsMobile, useOnEscapeKey } from 'src/custom-hooks';
import Select from 'react-select';

type propsType = {
    closeModal: () => void;
    currentUser?: typeUser;
    meetingsDoc?: typeMeetingsDoc;
    users: typeUser[];
};

export const UserAssignmentsModal: FC<propsType> = ({ closeModal, currentUser, meetingsDoc, users }) => {
    if ((!meetingsDoc && !currentUser) || (meetingsDoc && currentUser)) {
        return <></>;
    }
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<typeUser | null>(currentUser ?? null);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    const meetings: typeMeeting[] = useMemo(() =>
        meetingsDoc ?
            [...meetingsDoc.midweeks, ...meetingsDoc.publics].sort((m1, m2) => new Date(m2.date).getTime() - new Date(m1.date).getTime())
            :
            user?.meetingsWithAssignments ?? []
    , [meetingsDoc, user]);

    useOnEscapeKey(closeModal);

    const populateUser = (userId: number) => {
        setIsLoading(true);
        userServices.GetPopulatedUser(userId).then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.user) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setUser(response.data.user);
        });
    }

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        setIsLoading(true);
        userServices.GetPopulatedUser(currentUser.id).then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.user) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setUser(response.data.user);
        });
    }, []);

    return (
        <Modal show fullscreen onHide={closeModal}>
            <Modal.Header className={`${isDarkMode ? 'bg-lightdark text-white' : ''} border`} closeButton>
                <Modal.Title>
                    ASIGNACIONES POR USUARIO
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={isDarkMode ? 'bg-dark text-white' : ''}>
                <div className={'container mb-3'} style={{ maxWidth: '600px'}}>
                    <Select className={'text-dark mt-2'}
                        options={users}
                        getOptionLabel={u => `${u.name} ${u.lastName} (${u.email})`}
                        getOptionValue={u => u.id.toString()}
                        value={user}
                        onChange={u => u ? populateUser(u.id) : setUser(null)}
                        autoFocus
                        isDisabled={!!currentUser}
                    />
                    {user &&
                        <>
                        <div className={'text-center fw-bolder mt-2'}>
                            Grupo de Predicación: {user.group ?? '(No cargado)'} {user.groupIsTheOverseer && '- Superintendente de Grupo'} {user.groupIsTheOverseer && '- Auxiliar de Grupo'}
                        </div>
                        <div className={'text-center fw-bolder'}>
                            Roles: {user.rolesAsText}
                        </div>
                        </>
                    }
                </div>

                {isLoading &&
                    <Loading mt={'80px'} />
                }

                {user && !isLoading &&
                    <>
                    <Modal.Header className={`d-flex justify-content-center text-center ${isDarkMode ? 'bg-lightdark text-white' : ''} border`}>
                        <Modal.Title>
                            REUNIONES
                        </Modal.Title>
                    </Modal.Header> 
                    <div className={`${isDarkMode ? 'bg-lightdark text-white' : ''} border-start border-end p-3`}>
                        <div className={'row'}>
                            <div className={`col-lg card ${isDarkMode ? 'bg-lightdark text-white border-end' : ''} rounded-0`}>
                                <h4 className={'border-bottom pb-2'}>
                                    PARTICIPACIONES
                                </h4>
                                {!!meetings.filter(m => m.sections.some(s => s.segments.some(se => se.assignments.some(a => a.user?.id === user.id)))).length ?
                                    meetings.filter(m => m.sections.some(s => s.segments.some(se => se.assignments.some(a => a.user?.id === user.id)))).map(m =>
                                        <div key={m.id}>
                                            <span className={'fw-bolder me-1'}>
                                                {m.date} -
                                            </span>
                                            {m.sections.filter(s => s.segments.some(se => se.assignments.some(a => a.user?.id === user.id))).map(s =>
                                                <span key={s.title}>
                                                    <span className={'fw-bolder me-2'}>
                                                        {s.title}:
                                                    </span>
                                                    {s.segments.filter(se => se.assignments.some(a => a.user?.id === user.id)).map(se =>
                                                        <span key={se.subtitle}>
                                                            <span className={'me-1'}>
                                                                {se.subtitle}
                                                            </span>
                                                            {se.assignments.filter(a => a.user?.id === user.id).map(a =>
                                                                <span key={a.room + a.isHelper}>
                                                                    - {a.room ? 'Auditorio' : 'Sala Auxiliar'} {a.isHelper ? '(Ayudante)' : ''}
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    )
                                    :
                                    'Ninguna'
                                }
                            </div>
                            <div className={`col-lg-4 card ${isDarkMode ? 'bg-lightdark text-white' : ''} ${isMobile ? 'mt-4' : ''}`}>
                                <h4 className={'border-bottom pb-2'}>
                                    MECÁNICAS
                                </h4>
                                {meetings.filter(m => [...m.avAssignments, ...m.usherAssignments].some(_m => _m.user?.id === user.id)).length ?
                                    meetings.filter(m => [...m.avAssignments, ...m.usherAssignments].some(_m => _m.user?.id === user.id)).map(m =>
                                        <div key={m.id}>
                                            <span className={'fw-bolder me-1'}>
                                                {m.date} -
                                            </span>
                                            {[...m.avAssignments, ...m.usherAssignments].filter(a => a.user?.id === user.id).map(a =>
                                                <span key={a.name}>
                                                    {a.name}
                                                </span>
                                            )}
                                        </div>
                                    )
                                    :
                                    'Ninguna'
                                }
                            </div>
                        </div>
                    </div>
                    <Modal.Header className={`d-flex justify-content-center text-center ${isDarkMode ? 'bg-lightdark text-white' : ''} border`}>
                        <Modal.Title>
                            SERVICIO DEL CAMPO
                        </Modal.Title>
                    </Modal.Header> 
                    <div className={`${isDarkMode ? 'bg-dark text-white' : ''} border-start border-end p-3`}>
                        <div className={'row'}>
                            <div className={`col-lg ${isDarkMode ? 'text-white border-white' : ''} pb-2`}>
                                {!!user.publicMinistryAssignments?.filter(a => new Date(a.year, a.month) >= new Date(new Date().getFullYear(), new Date().getMonth())).length ?
                                    (user.publicMinistryAssignments
                                        .filter(a => new Date(a.year, a.month) >= new Date(new Date().getFullYear(), new Date().getMonth()))
                                        .map(a =>
                                            <div key={a.id} className={''}>
                                                <h5>
                                                    {months[a.month - 1]} {a.year}
                                                </h5>
                                                <p>
                                                    {a.isCircuitOverseer && '|VISITA|'} CONDUCTOR{a.captain2?.id === user.id ? ' SUPLENTE:' : ':'} {a.weekday !== null ? `Los ${daysOfTheWeekP[a.weekday]}` : `El ${helpers.GetDayOfWeekFromDateString(`${a.year}-${a.month}-${a.day}`)} ${a.day}`} {String(a.hour).padStart(2, '0')}:{String(a.minutes).padStart(2, '0')} hs. {a.address1 && `desde ${a.address1}`} {a.address2 && `o desde ${a.address2}`} ({typesOfPreaching.find(tp => tp.type === a.typeOfPreaching)?.label}) {a.groups && `, grupos ${a.groups.join(', ')}; ${!!a.territories.length && `territorios ${a.territories.join(', ')}`}`}
                                                </p>
                                            </div>
                                        )
                                    )
                                    :
                                    <h4>
                                        Ninguna
                                    </h4>
                                }
                            </div>
                        </div>
                    </div>
                    <Modal.Header className={`d-flex justify-content-center text-center ${isDarkMode ? 'bg-lightdark text-white' : ''} border`}>
                        <Modal.Title>
                            CARRITOS
                        </Modal.Title>
                    </Modal.Header> 
                    <div className={`${isDarkMode ? 'bg-dark text-white' : ''} border-start border-end p-3`}>
                        <div className={'row'}>
                            <div className={`col-lg ${isDarkMode ? 'text-white border-white' : ''} pb-2`}>
                                {!!user.cartAssignments?.length ?
                                    user.cartAssignments.map(a =>
                                        <div key={a.id}>
                                            <span className={'fw-bolder me-1'}>
                                                {daysOfTheWeekP[a.weekday]} de {months[a.month]} {a.year} {generalHelpers.GetTimeFromPeriodObject(a as typeCartPeriod)} con {a.participants?.find(x => x.userId !== user.id)?.name} {a.participants?.find(x => x.userId !== user.id)?.lastName} - {a.name}
                                            </span>                                    
                                        </div>
                                    )
                                    :
                                    'Ninguna'
                                }
                            </div>
                        </div>
                    </div>
                    <Modal.Header className={`d-flex justify-content-center text-center ${isDarkMode ? 'bg-lightdark text-white' : ''} border`}>
                        <Modal.Title>
                            CASA EN CASA
                        </Modal.Title>
                    </Modal.Header> 
                    <div className={`${isDarkMode ? 'bg-dark text-white' : ''} border-start border-end p-3`}>
                        {user.hthAssignments?.length ?
                            <h5>
                                {user.hthAssignments.length === config.numberOfTerritories ? 'Todos' : user.hthAssignments.join(', ')}
                            </h5>
                            :
                            'Ninguno'
                        }
                    </div>
                    <Modal.Header className={`d-flex justify-content-center text-center ${isDarkMode ? 'bg-lightdark text-white' : ''} border`}>
                        <Modal.Title>
                            TELEFÓNICA
                        </Modal.Title>
                    </Modal.Header> 
                    <div className={`${isDarkMode ? 'bg-dark text-white' : ''} border p-3`}>
                        {user.phoneAssignments?.length ?
                            <h5>
                                {user.phoneAssignments.length === config.numberOfTerritories ? 'Todos' : user.phoneAssignments.join(', ')}
                            </h5>
                            :
                            'Ninguno'
                        }
                    </div>
                    </>
                }
            </Modal.Body>
        </Modal>
    );
}
